import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { SystemConfig } from '@ensofinance/shortcuts-widget';

const COLOR_TXT_PRIMARY = '#111111'; // darkest
const COLOR_TXT_SECONDARY = '#747474';
const COLOR_TXT_TERTIARY = '#989898';
const COLOR_TXT_INVERSE = '#F0F0F0'; // lightest

const COLOR_BG_PRIMARY = '#FFFFFF'; // lightest
const COLOR_BG_SECONDARY = '#F9F9F9';
const COLOR_BG_TERTIARY = '#F0F0F0';
const COLOR_BG_DISABLED = '#E7E7E7';
const COLOR_BG_INVERSE = '#111111'; // darkest

const COLOR_ACCENT_YELLOW = '#FFF9DA';
const COLOR_ACCENT_BLUE = '#ECF2F8';

const COLOR_GUARDIANS_CAMPAIGN_PURPLE = '#463C78';

const customConfig = defineConfig({
  theme: {
    tokens: {
      fonts: {
        body: { value: 'Inter, system-ui' },
        heading: { value: 'Lufga, sans-serif' },
      },
      colors: {
        brandBlue: {
          DEFAULT: { value: '#E7F1FA' },
          800: { value: '#11475A' },
        },
        brandIndigo: {
          DEFAULT: { value: '#5A3CE8' },
          500: { value: '#5A3CE8' },
        },
      },
    },
    semanticTokens: {
      colors: {
        // custom semantic token defined by plume branding
        // it would be nice to match our brand tokens exactly
        // but only the default semantic token values will work with
        // chakra ui's colorPalette functionality
        text: {
          primary: { value: COLOR_TXT_PRIMARY },
          secondary: { value: COLOR_TXT_SECONDARY },
          tertiary: { value: COLOR_TXT_TERTIARY },
          inverse: { value: COLOR_TXT_INVERSE },
          positive: { value: '#007A4D' },
        },
        // override of chakra default semantic token to match plume branding
        fg: {
          DEFAULT: { value: COLOR_TXT_PRIMARY }, // darkest
          muted: { value: COLOR_TXT_SECONDARY },
          subtle: { value: COLOR_TXT_TERTIARY },
          inverted: { value: COLOR_TXT_INVERSE }, // lightest
        },
        // override of chakra default semantic token to match plume branding
        border: {
          DEFAULT: { value: COLOR_BG_TERTIARY },
          subtle: { value: COLOR_BG_SECONDARY },
          muted: { value: COLOR_BG_TERTIARY },
          emphasized: { value: COLOR_BG_DISABLED },
          inverted: { value: COLOR_BG_INVERSE },
        },
        // override of chakra default semantic token to match plume branding
        bg: {
          DEFAULT: { value: COLOR_BG_PRIMARY }, // lightest
          subtle: { value: COLOR_BG_SECONDARY },
          muted: { value: COLOR_BG_TERTIARY },
          emphasized: { value: COLOR_BG_DISABLED },
          accent: { value: '#F9FAFB' },
          inverted: { value: COLOR_BG_INVERSE }, // darkest
          accent2: { value: COLOR_ACCENT_YELLOW }, // used for claim section accent
          accent3: { value: COLOR_ACCENT_BLUE }, // used for leaderboard & profile section accent
          darkBrown: { value: '#221A16' },
          darkBlue: { value: '#242E44' },
          guardiansCampaign: { value: COLOR_GUARDIANS_CAMPAIGN_PURPLE },
        },
      },
    },
  },
});

export const system = createSystem(defaultConfig, customConfig);

export const ensoWidgetTheme: SystemConfig = {
  theme: {
    tokens: {
      fonts: {
        body: { value: 'Inter, system-ui' },
        heading: { value: 'Lufga, sans-serif' },
      },
      spacing: {
        1: { value: '12px' },
        2: { value: '20px' },
      },
      colors: {
        blue: {
          default: { value: COLOR_BG_INVERSE },
          600: { value: COLOR_BG_INVERSE },
        },
      },
      radii: {
        none: { value: '0px' },
        sm: { value: '2px' },
        md: { value: '8px' },
        lg: { value: '24px' },
        xl: { value: '20px' },
        full: { value: '9999px' },
      },
    },
    semanticTokens: {
      colors: {
        border: {
          DEFAULT: { value: COLOR_BG_PRIMARY },
          subtle: { value: COLOR_BG_TERTIARY },
          muted: { value: COLOR_BG_SECONDARY },
          emphasized: { value: COLOR_BG_DISABLED },
          inverted: { value: COLOR_BG_INVERSE },
        },
        bg: {
          subtle: { value: COLOR_BG_PRIMARY },
        },
      },
    },
  },
};
